/**
 * MultaPublic.jsx — Página pública /multas/{slug} de una simulación.
 * Anónima: nunca muestra nombre de empresa ni datos identificables.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import MultaResultCard from "@/components/MultaResultCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MultaPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/multas/${slug}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "404" : "Error"));
  }, [slug]);

  if (err === "404") {
    return (
      <div className="min-h-[60vh] px-6 py-20 max-w-2xl mx-auto text-center">
        <SEO title="Simulación no encontrada — Ley de Datos Chile" />
        <p className="font-mono text-[10px] tracking-[0.22em] text-slate-500 uppercase mb-3">Error · 404</p>
        <h1 className="font-display text-4xl text-slate-900 tracking-tight">Simulación no encontrada</h1>
        <p className="mt-3 text-slate-600">Este enlace no existe o fue retirado.</p>
        <Link to="/simulador-multas" className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors">
          Hacer mi simulación <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-[60vh] grid place-items-center text-sm font-mono uppercase tracking-[0.22em] text-slate-500">Cargando…</div>;
  }

  const r = data.resultado;
  const pageUrl = `https://leydedatoschile.com/multas/${slug}`;
  const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/multas/${slug}/og.svg`;
  const shareText = encodeURIComponent("Mira esta simulación de multas Ley 21.719:");

  return (
    <section data-testid="multa-publica">
      <SEO
        title={`Exposición Ley 21.719: ${r.escenario === "amonestacion_pyme_primera_vez" ? "Amonestación" : `${r.monto_min_fmt} - ${r.monto_max_fmt}`} · Simulador`}
        description={`Simulación anónima de exposición a multas bajo la Ley N° 21.719: ${r.categoria_label || "Leve"}. Calcula tu propia exposición gratis.`}
        canonical={pageUrl}
        image={ogUrl}
      />

      {/* Hero dark */}
      <div className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-12 lg:pt-20">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-5">
            <span className="live-dot inline-block" />
            Simulación anónima · Ley 21.719
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight">
            Exposición estimada en este <em className="italic text-blue-300">escenario</em>.
          </h1>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
            {data.es_pyme ? "PYME" : "No-PYME"} · {data.reincidente ? "Reincidente" : "Primera vez"} · resultado anónimo
          </p>
        </div>
      </div>

      <div className="px-6 py-12 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <MultaResultCard resultado={r} />

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3">Compartir</p>
              <div className="flex flex-wrap gap-2">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-multa-ln" className="h-10 px-4 rounded-full bg-[#0a66c2] hover:bg-[#0958ad] text-white text-sm font-semibold inline-flex items-center transition-colors">LinkedIn</a>
                <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-multa-wa" className="h-10 px-4 rounded-full bg-[#25d366] hover:bg-[#20bd5b] text-white text-sm font-semibold inline-flex items-center transition-colors">WhatsApp</a>
                <button data-testid="share-multa-copy" onClick={() => navigator.clipboard?.writeText(pageUrl)} className="h-10 px-4 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors">Copiar link</button>
              </div>
            </div>

            <div className="rounded-2xl bg-ink-950 text-white p-5 sm:p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
              <div className="relative">
                <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Educativo
                </p>
                <p className="text-sm text-white/80 leading-relaxed">Calcula tu propia exposición o escanea tu sitio para ver hallazgos reales.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/simulador-multas" className="h-9 px-4 rounded-full bg-white text-slate-900 text-sm font-semibold inline-flex items-center hover:bg-slate-100 transition-colors">Hacer mi simulación</Link>
                  <Link to="/" className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold inline-flex items-center transition-colors">Escanear mi sitio</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
